import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Activity, AlertTriangle, Download, FolderLock, HeartPulse, Layers, Loader, MapPin, RefreshCw,
  ScrollText, Server, Users,
} from 'lucide-react';
import { PageHeader } from '../components/ui/PageHeader';
import { StatTile } from '../components/ui/StatTile';
import { EmptyState } from '../components/ui/EmptyState';
import { SectionHeader } from '../components/ui/SectionHeader';
import { LogbookFilters } from '../components/logbook/LogbookFilters';
import { LogbookEventList } from '../components/logbook/LogbookEventList';
import { LogbookBreakdown } from '../components/logbook/LogbookBreakdown';
import { LogbookActivityChart } from '../components/logbook/LogbookActivityChart';
import { LogbookCompleteness } from '../components/logbook/LogbookCompleteness';
import { LogbookViewBar } from '../components/logbook/LogbookViewBar';
import { AddToCaseModal } from '../components/logbook/AddToCaseModal';
import { PanelTour } from '../components/ui/PanelTour';
import { logbookPanelTour } from '../config/panelTours';
import { useTourNavigation } from '../hooks/useTourNavigation';
import { useAuth } from '../contexts/AuthContext';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { useLogbookServers } from '../hooks/useLogbookServers';
import { logbookApiService, LogbookApiError } from '../services/logbookApi';
import { canAdministerLogbook, canExportLogbook, canProtectLogbook, canViewLogbook } from '../utils/logbookPermissions';
import { formatCount, isEvidenceGap } from '../utils/logbook';
import {
  DEFAULT_LOGBOOK_FILTERS, boxAround, filtersFromParams, filtersToParams, isUnfiltered,
  toAggregationQuery, toSearchQuery, type LogbookFilterState,
} from '../utils/logbookFilters';
import {
  groupEvents, partitionEvents, viewFromParams, viewToParams, type LogbookViewOptions,
} from '../utils/logbookView';
import type { LogbookAggregationResult, LogbookEvent, LogbookGroupBy, LogbookSearchResult } from '../types/logbook';

/**
 * The Logbook investigation workbench.
 *
 * A search answers one question, so the page is built around narrowing: the filters state the
 * question, the aggregations describe the whole matching set, and the list shows the records. The
 * breakdowns and the activity chart are inputs as well as outputs, because narrowing by clicking the
 * biggest bar is how an investigation actually moves.
 *
 * Counts never come from the page. A page is 200 records at most and is pinned to a snapshot, while
 * an aggregation counts every matching event in one read transaction. Mixing the two would let a
 * tile say 200 when the answer is forty thousand.
 */

const SECONDARY_GROUPS: { id: LogbookGroupBy; label: string }[] = [
  { id: 'actor_id', label: 'Actors' },
  { id: 'object_id', label: 'Objects' },
  { id: 'server_id', label: 'Servers' },
  { id: 'dimension', label: 'Dimensions' },
  { id: 'outcome', label: 'Outcomes' },
];

export function LogbookView() {
  useDocumentTitle('Logbook');
  const { identity, isLoading: authLoading } = useAuth();
  const canView = canViewLogbook(identity);
  const canProtect = canProtectLogbook(identity);
  const canExport = canExportLogbook(identity);
  const canAdminister = canAdministerLogbook(identity);
  const { tourNavigation } = useTourNavigation();

  const [params, setParams] = useSearchParams();
  const { servers, byId: serversById, clusters, error: serversError } = useLogbookServers();

  const [filters, setFilters] = useState<LogbookFilterState>(() => filtersFromParams(params));
  const [view, setView] = useState<LogbookViewOptions>(() => viewFromParams(params));
  const [page, setPage] = useState<LogbookSearchResult | null>(null);
  const [events, setEvents] = useState<LogbookEvent[]>([]);
  const [byAction, setByAction] = useState<LogbookAggregationResult | null>(null);
  const [secondary, setSecondary] = useState<LogbookAggregationResult | null>(null);
  const [overTime, setOverTime] = useState<LogbookAggregationResult | null>(null);
  const [secondaryGroup, setSecondaryGroup] = useState<LogbookGroupBy>('actor_id');
  const [loading, setLoading] = useState(true);
  const [paging, setPaging] = useState(false);
  const [error, setError] = useState<LogbookApiError | Error | null>(null);
  const [held, setHeld] = useState<Set<string>>(new Set());
  const [attaching, setAttaching] = useState<LogbookEvent[] | null>(null);
  const [caseNote, setCaseNote] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  const [exportNote, setExportNote] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);
  const firstRender = useRef(true);

  // The filters are the URL, so a search is a link a colleague can open unchanged.
  useEffect(() => {
    if (firstRender.current) { firstRender.current = false; return; }
    const next = filtersToParams(filters);
    viewToParams(view, next);
    setParams(next, { replace: true });
  }, [filters, view, setParams]);

  useEffect(() => {
    const controller = new AbortController();
    let cancelled = false;
    setLoading(true);
    setError(null);

    const run = async () => {
      const now = Date.now();
      try {
        const [result, actionCounts, groupCounts, timeCounts] = await Promise.all([
          logbookApiService.search(toSearchQuery(filters, now), controller.signal),
          logbookApiService.aggregate(toAggregationQuery(filters, 'action', now), controller.signal).catch(() => null),
          logbookApiService.aggregate(toAggregationQuery(filters, secondaryGroup, now), controller.signal).catch(() => null),
          logbookApiService.aggregate(toAggregationQuery(filters, 'time', now), controller.signal).catch(() => null),
        ]);
        if (cancelled) return;
        setPage(result);
        setEvents(result.events);
        setByAction(actionCounts);
        setSecondary(groupCounts);
        setOverTime(timeCounts);
      } catch (caught) {
        if (cancelled || controller.signal.aborted) return;
        setPage(null);
        setEvents([]);
        setByAction(null);
        setSecondary(null);
        setOverTime(null);
        setError(caught instanceof Error ? caught : new Error('That search could not be completed.'));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    void run();
    return () => { cancelled = true; controller.abort(); };
  }, [filters, secondaryGroup, nonce]);

  const loadMore = useCallback(async () => {
    if (!page?.cursor || paging) return;
    setPaging(true);
    try {
      const next = await logbookApiService.search({ ...toSearchQuery(filters), cursor: page.cursor });
      setPage(next);
      setEvents((current) => [...current, ...next.events]);
    } catch (caught) {
      setError(caught instanceof Error ? caught : new Error('The next page could not be loaded.'));
    } finally {
      setPaging(false);
    }
  }, [page, paging, filters]);

  /** Export runs as a job: the service answers one bounded NDJSON page to the job owner. */
  const runExport = useCallback(async () => {
    if (exporting) return;
    setExporting(true);
    setExportNote(null);
    try {
      const submitted = await logbookApiService.submitJob('export', toSearchQuery(filters));
      const job = await logbookApiService.awaitJob(submitted.job_id);
      if (job.state !== 'ready' || !job.result || !('ndjson' in job.result)) {
        setExportNote('The export was cancelled before it produced a page.');
        return;
      }
      const blob = new Blob([job.result.ndjson], { type: 'application/x-ndjson' });
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = `logbook-${new Date().toISOString().slice(0, 19).replace(/[:T]/g, '')}.ndjson`;
      document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();
      URL.revokeObjectURL(url);
      setExportNote(job.result.cursor
        ? 'One page exported. More records match; narrow the filters and export again.'
        : 'Export complete.');
    } catch (caught) {
      setExportNote(caught instanceof LogbookApiError ? caught.message : 'The export could not be completed.');
    } finally {
      setExporting(false);
    }
  }, [exporting, filters]);

  const applyGroupFilter = useCallback((groupBy: LogbookGroupBy, value: string) => {
    setFilters((current) => {
      switch (groupBy) {
        case 'action': return { ...current, actions: [value] };
        case 'actor_id': return { ...current, actorId: value, actorName: '' };
        case 'server_id': return { ...current, servers: [value] };
        case 'object_id': return { ...current, objectId: value };
        case 'dimension': return { ...current, dimension: value };
        case 'outcome': return { ...current, outcome: value as LogbookFilterState['outcome'] };
        case 'namespace': return { ...current, namespace: value };
        default: return current;
      }
    });
  }, []);

  const totals = useMemo(() => {
    const matched = (byAction?.groups ?? []).reduce((sum, group) => sum + group.event_count, 0);
    return {
      matched,
      actions: byAction?.groups.length ?? 0,
      gaps: events.filter(isEvidenceGap).length,
      partial: byAction?.state === 'partial',
    };
  }, [byAction, events]);

  // Presentation, not a query: the page is filtered and folded here, and the counts stay on screen.
  const partition = useMemo(() => partitionEvents(events, view, held), [events, view, held]);
  const runs = useMemo(() => groupEvents(partition.visible, view.groupWindowMs), [partition.visible, view.groupWindowMs]);

  const oldestReceipt = useMemo(() => {
    const receipts = (page?.scope ?? [])
      .map((serverId) => serversById.get(serverId)?.lastReceivedAt)
      .filter((value): value is number => typeof value === 'number');
    return receipts.length > 0 ? Math.min(...receipts) : null;
  }, [page, serversById]);

  const notConfigured = error instanceof LogbookApiError && error.isNotConfigured;
  const tooBroad = error instanceof LogbookApiError && error.isTooBroad;

  return (
    <div className="space-y-6">
      <PageHeader
        eyebrow="Moderation"
        title="Logbook"
        description="Server-side evidence from every pack running Logbook."
        icon={ScrollText}
        meta={page ? (
          <span className="badge badge-neutral tabular-nums">{formatCount(page.scope.length)} servers in scope</span>
        ) : undefined}
        actions={(
          <>
            <PanelTour
              tourId="logbook"
              title="Logbook"
              steps={logbookPanelTour(canProtect, canExport, canAdminister)}
              enabled={canView && !authLoading && !notConfigured}
              {...tourNavigation}
            />
            <Link to="/logbook/nearby" className="glass-button" data-tour="logbook-nearby">
              <MapPin className="w-4 h-4" />
              Who was here
            </Link>
            <Link to="/logbook/cases" className="glass-button" data-tour="logbook-cases">
              <FolderLock className="w-4 h-4" />
              Cases
            </Link>
            {canAdminister && (
              <Link to="/logbook/health" className="glass-button" data-tour="logbook-health">
                <HeartPulse className="w-4 h-4" />
                Evidence health
              </Link>
            )}
            {canExport && (
              <button type="button" className="glass-button" data-tour="logbook-export" onClick={runExport} disabled={exporting || events.length === 0}>
                {exporting ? <Loader className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                Export
              </button>
            )}
            <button type="button" className="btn-primary" onClick={() => setNonce((value) => value + 1)} disabled={loading}>
              <RefreshCw className={loading ? 'w-4 h-4 animate-spin' : 'w-4 h-4'} />
              Run search
            </button>
          </>
        )}
      />

      <LogbookFilters
        value={filters}
        onChange={setFilters}
        onReset={() => setFilters({ ...DEFAULT_LOGBOOK_FILTERS, servers: [], actions: [], box: null })}
        servers={servers}
        clusters={clusters}
        disabled={notConfigured}
      />

      {serversError && !notConfigured && (
        <div className="alert alert-warning">
          <AlertTriangle className="w-4 h-4 shrink-0" />
          <span>The server list could not be loaded, so results show raw server ids. {serversError}</span>
        </div>
      )}

      {caseNote && (
        <div className="alert alert-info">
          <FolderLock className="w-4 h-4 shrink-0" />
          <span className="flex-1">{caseNote}</span>
          <Link to="/logbook/cases" className="link text-xs shrink-0">Open cases</Link>
        </div>
      )}

      {exportNote && (
        <div className="alert alert-info">
          <Download className="w-4 h-4 shrink-0" />
          <span>{exportNote}</span>
        </div>
      )}

      {notConfigured ? (
        <EmptyState
          icon={ScrollText}
          title="Logbook is not connected yet"
          description={error.message}
        />
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-tour="logbook-totals">
            <StatTile
              label="Matching events"
              value={formatCount(totals.matched)}
              icon={Activity}
              tone="accent"
              loading={loading}
              hint={totals.partial ? 'At least this many' : undefined}
            />
            <StatTile label="Distinct actions" value={formatCount(totals.actions)} icon={Layers} tone="info" loading={loading} />
            <StatTile
              label={secondaryGroup === 'actor_id' ? 'Distinct actors' : 'Groups'}
              value={formatCount(secondary?.groups.length ?? 0)}
              icon={secondaryGroup === 'actor_id' ? Users : Server}
              tone="purple"
              loading={loading}
            />
            <StatTile
              label="Capture gaps"
              value={formatCount(totals.gaps)}
              icon={AlertTriangle}
              tone={totals.gaps > 0 ? 'warning' : 'neutral'}
              loading={loading}
              hint={totals.gaps > 0 ? 'Evidence is incomplete here' : undefined}
            />
          </div>

          <div className="glass-card p-4 md:p-5" data-tour="logbook-activity">
            <LogbookActivityChart
              aggregation={overTime}
              loading={loading}
              onBrush={(startMs, endMs) => setFilters((current) => ({
                ...current,
                windowId: 'custom',
                after: Math.floor(startMs),
                before: Math.ceil(endMs),
              }))}
            />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3" data-tour="logbook-breakdowns">
            <LogbookBreakdown
              title="By action"
              icon={Layers}
              aggregation={byAction}
              loading={loading}
              onSelect={applyGroupFilter}
            />
            <div className="glass-card p-4 md:p-5">
              <SectionHeader
                title="Breakdown"
                icon={Users}
                actions={(
                  <div className="segmented">
                    {SECONDARY_GROUPS.map((group) => (
                      <button
                        key={group.id}
                        type="button"
                        className="segmented-item segmented-item-sm"
                        data-active={secondaryGroup === group.id}
                        onClick={() => setSecondaryGroup(group.id)}
                      >
                        {group.label}
                      </button>
                    ))}
                  </div>
                )}
              />
              <LogbookBreakdown
                bare
                title="Breakdown"
                aggregation={secondary}
                loading={loading}
                serversById={serversById}
                onSelect={applyGroupFilter}
              />
            </div>
          </div>

          <div className="glass-card overflow-hidden" data-tour="logbook-evidence">
            <div className="p-4 md:p-5 pb-3 space-y-3">
              <SectionHeader
                title="Evidence"
                icon={ScrollText}
                count={partition.visible.length}
              />
              <LogbookCompleteness
                completeness={page?.completeness}
                scope={page?.scope}
                snapshot={page?.snapshot}
                gapCount={totals.gaps}
                oldestReceiptAt={oldestReceipt}
                data-tour="logbook-coverage"
              />
              <div data-tour="logbook-view-bar">
                <LogbookViewBar value={view} onChange={setView} partition={partition} runCount={runs.length} />
              </div>
            </div>

            {error && events.length === 0 && !loading ? (
              <div className="px-4 pb-6">
                <EmptyState
                  bare
                  tone="danger"
                  icon={AlertTriangle}
                  title={tooBroad ? 'That search was too broad' : 'The search could not be completed'}
                  description={error.message}
                  action={(
                    <button type="button" className="glass-button" onClick={() => setNonce((value) => value + 1)}>
                      <RefreshCw className="w-4 h-4" />
                      Try again
                    </button>
                  )}
                />
              </div>
            ) : runs.length === 0 && !loading ? (
              <div className="px-4 pb-6">
                <EmptyState
                  bare
                  icon={ScrollText}
                  title={partition.hiddenTotal > 0 ? 'Everything on this page is hidden' : 'No evidence matched'}
                  description={partition.hiddenTotal > 0
                    ? `${formatCount(partition.hiddenTotal)} records are hidden by the view settings above.`
                    : isUnfiltered(filters)
                      ? 'Nothing was captured in this window.'
                      : 'Widen the window or drop a filter.'}
                />
              </div>
            ) : (
              <LogbookEventList
                runs={runs}
                serversById={serversById}
                loading={loading}
                canProtect={canProtect}
                heldEventIds={held}
                onAddToCase={setAttaching}
                onFilterActor={(event) => setFilters((current) => ({ ...current, actorId: event.actor_id, actorName: '' }))}
                onFilterAction={(event) => setFilters((current) => ({ ...current, actions: [event.action] }))}
                onFilterNearby={(event) => setFilters((current) => ({
                  ...current,
                  actions: [],
                  actorId: '',
                  actorName: '',
                  dimension: event.dimension,
                  servers: [event.server_id],
                  box: boxAround(event.x, event.y, event.z, 16),
                }))}
              />
            )}

            {page?.cursor && (
              <div className="p-4 border-t border-glass-200/10 flex flex-wrap items-center justify-between gap-3">
                <span className="text-xs text-glass-500">
                  {formatCount(partition.visible.length)} shown of {formatCount(events.length)} loaded.
                </span>
                <button type="button" className="glass-button-sm" onClick={loadMore} disabled={paging}>
                  {paging ? <Loader className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
                  Load more
                </button>
              </div>
            )}
          </div>
        </>
      )}

      {attaching && (
        <AddToCaseModal
          events={attaching}
          onClose={() => setAttaching(null)}
          onAttached={(result, target) => {
            // The lock on a row comes from what this session attached; nothing is re-fetched to
            // discover it, because a page of results is not worth a lookup per row.
            setHeld((current) => {
              const next = new Set(current);
              for (const event of attaching) next.add(event.event_id);
              return next;
            });
            setCaseNote(
              `${result.attached} ${result.attached === 1 ? 'record' : 'records'} copied into ${target.reference}`
              + (result.duplicates > 0 ? `, ${result.duplicates} already there.` : '.'),
            );
          }}
        />
      )}
    </div>
  );
}
